import { useCallback, useEffect, useRef, useState } from 'react'
import type { Banner } from '@nexbit/types'
import {
  apiAdminGetBanners, apiAdminCreateBanner, apiAdminUpdateBanner, apiAdminDeleteBanner, bannerImageUrl,
  ApiRequestError,
} from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  AlertCircle, ArrowDown, ArrowUp, Check, Image as ImageIcon, Loader2, Plus, Trash2, Upload, X,
} from 'lucide-react'

const MAX_BYTES = 2 * 1024 * 1024
const ACCEPT = 'image/webp,image/png,image/jpeg,image/gif'

/** Read a picked file as base64 (without the data: prefix the server strips anyway). */
function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new Error('อ่านไฟล์ไม่สำเร็จ'))
    reader.readAsDataURL(file)
  })
}

interface FormState {
  id: string | null
  title: string
  linkUrl: string
  isActive: boolean
  imageBase64: string | null
  previewUrl: string | null
}

const EMPTY: FormState = { id: null, title: '', linkUrl: '', isActive: true, imageBase64: null, previewUrl: null }

/**
 * Banners shown on the member home page. Images are stored in the database and served
 * through a cached endpoint, so a change here is live on the next page load — no deploy
 * and no file to copy onto the server.
 */
export default function AdminBannersPage() {
  const [banners, setBanners] = useState<Banner[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY)
  const [saving, setSaving] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      setBanners(await apiAdminGetBanners())
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : 'โหลดรายการแบนเนอร์ไม่สำเร็จ')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { void load() }, [load])

  async function pickFile(file: File | undefined) {
    if (!file) return
    setError(null)
    if (file.size > MAX_BYTES) { setError('ไฟล์ต้องไม่เกิน 2 MB'); return }
    try {
      const dataUrl = await readAsBase64(file)
      setForm((f) => ({ ...f, imageBase64: dataUrl, previewUrl: dataUrl }))
    } catch {
      setError('อ่านไฟล์ไม่สำเร็จ')
    }
  }

  function editBanner(b: Banner) {
    setForm({
      id: b.id, title: b.title, linkUrl: b.linkUrl ?? '', isActive: b.isActive,
      imageBase64: null, previewUrl: bannerImageUrl(b),
    })
    setNotice(null); setError(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  function resetForm() {
    setForm(EMPTY)
    if (fileRef.current) fileRef.current.value = ''
  }

  async function save() {
    if (!form.title.trim()) { setError('ต้องใส่ชื่อแบนเนอร์'); return }
    if (!form.id && !form.imageBase64) { setError('ต้องเลือกไฟล์รูป'); return }
    setSaving(true); setError(null); setNotice(null)
    try {
      const body = {
        title: form.title.trim(),
        linkUrl: form.linkUrl.trim() || null,
        isActive: form.isActive,
        ...(form.imageBase64 ? { imageBase64: form.imageBase64 } : {}),
      }
      if (form.id) {
        await apiAdminUpdateBanner(form.id, body)
        setNotice('บันทึกแบนเนอร์แล้ว')
      } else {
        await apiAdminCreateBanner({ ...body, sortOrder: banners.length })
        setNotice('เพิ่มแบนเนอร์แล้ว')
      }
      resetForm()
      await load()
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : 'บันทึกไม่สำเร็จ')
    } finally { setSaving(false) }
  }

  async function patch(b: Banner, body: Parameters<typeof apiAdminUpdateBanner>[1]) {
    setBusyId(b.id); setError(null)
    try {
      await apiAdminUpdateBanner(b.id, body)
      await load()
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : 'อัปเดตไม่สำเร็จ')
    } finally { setBusyId(null) }
  }

  /** Swap sort_order with the neighbour so the carousel order is what the list shows. */
  async function move(index: number, dir: -1 | 1) {
    const a = banners[index]
    const b = banners[index + dir]
    if (!a || !b) return
    setBusyId(a.id); setError(null)
    try {
      await apiAdminUpdateBanner(a.id, { title: a.title, sortOrder: b.sortOrder })
      await apiAdminUpdateBanner(b.id, { title: b.title, sortOrder: a.sortOrder })
      await load()
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : 'จัดลำดับไม่สำเร็จ')
    } finally { setBusyId(null) }
  }

  async function remove(b: Banner) {
    if (!window.confirm(`ลบแบนเนอร์ "${b.title}" ?`)) return
    setBusyId(b.id); setError(null)
    try {
      await apiAdminDeleteBanner(b.id)
      if (form.id === b.id) resetForm()
      await load()
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : 'ลบไม่สำเร็จ')
    } finally { setBusyId(null) }
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl">
      <header className="mb-5">
        <h1 className="text-lg font-semibold tracking-tight">แบนเนอร์หน้าแรก</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          รูปสไลด์บนสุดของหน้าแรกฝั่งสมาชิก — บันทึกแล้วมีผลทันที
        </p>
      </header>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-[var(--radius-sm)] border border-destructive/40 bg-card p-2" role="alert">
          <AlertCircle className="h-3.5 w-3.5 shrink-0 text-destructive" />
          <p className="font-mono text-xs text-destructive">{error}</p>
        </div>
      )}
      {notice && (
        <div className="mb-4 flex items-center gap-2 rounded-[var(--radius-sm)] border border-success/40 bg-card p-2" role="status">
          <Check className="h-3.5 w-3.5 shrink-0 text-success" />
          <p className="font-mono text-xs text-success">{notice}</p>
        </div>
      )}

      {/* ── Editor ─────────────────────────────────────────────────────────── */}
      <section className="mb-6 overflow-hidden rounded-[var(--radius)] border border-border bg-card shadow-[var(--shadow-sm)]">
        <div className="flex items-center gap-2 border-b border-border bg-secondary/40 px-4 py-3">
          <ImageIcon className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold">{form.id ? 'แก้ไขแบนเนอร์' : 'เพิ่มแบนเนอร์'}</span>
          {form.id && (
            <Button variant="ghost" size="sm" className="ml-auto h-7 gap-1 text-xs" onClick={resetForm}>
              <X className="h-3 w-3" /> ยกเลิกการแก้ไข
            </Button>
          )}
        </div>

        <div className="grid gap-4 p-4 md:grid-cols-[220px_1fr]">
          {/* Preview + picker */}
          <div className="flex flex-col gap-2">
            <div className="flex aspect-[16/7] items-center justify-center overflow-hidden rounded-[var(--radius-sm)] border border-dashed border-border bg-muted/40">
              {form.previewUrl ? (
                <img src={form.previewUrl} alt="ตัวอย่างแบนเนอร์" className="h-full w-full object-cover" />
              ) : (
                <span className="font-mono text-[11px] text-muted-foreground">ยังไม่ได้เลือกรูป</span>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => void pickFile(e.target.files?.[0])}
            />
            <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => fileRef.current?.click()}>
              <Upload className="h-3.5 w-3.5" /> {form.id ? 'เปลี่ยนรูป' : 'เลือกรูป'}
            </Button>
            <p className="font-mono text-[10px] text-muted-foreground">
              WEBP / PNG / JPEG / GIF · ไม่เกิน 2 MB · แนะนำ 1200×520
            </p>
          </div>

          {/* Fields */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor="banner-title" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                ชื่อแบนเนอร์
              </Label>
              <Input
                id="banner-title"
                value={form.title}
                maxLength={120}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="เช่น หวยรัฐบาลจ่ายสูง 3 ตัว 920"
                className="h-9 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="banner-link" className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                ลิงก์เมื่อกด (ไม่บังคับ)
              </Label>
              <Input
                id="banner-link"
                value={form.linkUrl}
                maxLength={500}
                onChange={(e) => setForm((f) => ({ ...f, linkUrl: e.target.value }))}
                placeholder="/lottery/government"
                className="h-9 font-mono text-sm"
              />
            </div>
            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                className="h-4 w-4 accent-[var(--primary)]"
              />
              แสดงบนหน้าแรก
            </label>
            <div>
              <Button size="sm" className="gap-1.5 text-xs" disabled={saving} onClick={() => void save()}>
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                {form.id ? 'บันทึกการแก้ไข' : 'เพิ่มแบนเนอร์'}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ── List ───────────────────────────────────────────────────────────── */}
      <section className="overflow-hidden rounded-[var(--radius)] border border-border bg-card shadow-[var(--shadow-sm)]">
        <div className="border-b border-border bg-secondary/40 px-4 py-3">
          <span className="text-sm font-semibold">แบนเนอร์ทั้งหมด ({banners.length})</span>
        </div>

        {loading ? (
          <div className="space-y-2 p-4">
            {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
          </div>
        ) : banners.length === 0 ? (
          <p className="p-8 text-center font-mono text-xs text-muted-foreground">
            ยังไม่มีแบนเนอร์ — เพิ่มรูปแรกด้านบน
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {banners.map((b, i) => (
              <li key={b.id} className="flex items-center gap-3 p-3">
                <img
                  src={bannerImageUrl(b)}
                  alt=""
                  className={cn('h-14 w-32 shrink-0 rounded-[var(--radius-sm)] border border-border object-cover', !b.isActive && 'opacity-40 grayscale')}
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{b.title}</p>
                  <p className="truncate font-mono text-[11px] text-muted-foreground">{b.linkUrl || '— ไม่มีลิงก์'}</p>
                </div>
                <span className={cn('rounded-full px-2 py-0.5 font-mono text-[10px]',
                  b.isActive ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground')}>
                  {b.isActive ? 'แสดงอยู่' : 'ซ่อน'}
                </span>
                <div className="flex shrink-0 items-center gap-1">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" aria-label={`เลื่อนขึ้น ${b.title}`}
                    disabled={i === 0 || busyId !== null} onClick={() => void move(i, -1)}>
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" aria-label={`เลื่อนลง ${b.title}`}
                    disabled={i === banners.length - 1 || busyId !== null} onClick={() => void move(i, 1)}>
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs"
                    disabled={busyId !== null} onClick={() => void patch(b, { title: b.title, isActive: !b.isActive })}>
                    {b.isActive ? 'ซ่อน' : 'แสดง'}
                  </Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => editBanner(b)}>
                    แก้ไข
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                    aria-label={`ลบ ${b.title}`} disabled={busyId !== null} onClick={() => void remove(b)}>
                    {busyId === b.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
